import { useFormApi } from 'informed';
import { useReduxState } from 'hooks';
import api from 'api';
import { getCustomAttributesTypes } from 'lib/customAttributes';
import {
  Checkbox,
  FormGroup,
  LanguageVariants,
  NumberStepper,
  Select,
  SortableArrayList,
  Text
} from 'components/common/form';

import type { CreateProductUserCustomAttributeReq } from 'shared-types/requests';

export type CustomAttributesFormType = Omit<
  CreateProductUserCustomAttributeReq,
  'position'
> & { publicEventsOnly?: boolean; publicEventsOnlyOptional?: boolean };

interface Props {
  hideAnalyticsControl?: boolean;
  hidePublicEventsOnlyControl?: boolean;
  urlState?: string;
}

const ProductUserCustomAttributesForm = ({
  hideAnalyticsControl = true,
  hidePublicEventsOnlyControl = true,
  urlState
}: Props) => {
  const { messages, user } = useReduxState();

  const formApi = useFormApi<CustomAttributesFormType>();

  const validateTitle = async (value: string) => {
    if (!value || !value?.toString()?.trim()) {
      return messages.commonFieldRequired;
    }
    if (!urlState) return;

    try {
      await api.head(
        `/admin/product-user-custom-attributes/${urlState}?title=${value}`
      );
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  const formState = formApi.getState();

  const attributeTypes = getCustomAttributesTypes({ messages });

  const userIsSuperadmin = user?.roles.includes('superadmin');

  return (
    <>
      <div className="row-sm">
        <div className="col">
          <FormGroup label={messages.commonTitle}>
            <Text
              customValidateAsync={validateTitle}
              field="title"
              id="title"
              required={true}
              validateOnChange={true}
            />

            <LanguageVariants field="titleTranslations" type="text" />
          </FormGroup>
        </div>
      </div>

      <div className="row-sm">
        <div className="col">
          <FormGroup label={messages.commonRequired}>
            <Checkbox
              field="isRequired"
              id="is-required"
              label={messages.commonFieldRequired}
              stopPropagation={true}
            />
          </FormGroup>
        </div>

        {userIsSuperadmin && !hidePublicEventsOnlyControl ? (
          <div className="col">
            <FormGroup
              label={messages.accessAndVisibilityVisibilityOptionPublic}
            >
              <Checkbox
                field="publicEventsOnly"
                id="public-events-only"
                label={
                  messages.accessAndVisibilityVisibilityOptionPublicEventsOnly
                }
                labelTooltip={
                  messages.accessAndVisibilityVisibilityOptionPublicEventsOnlyHelpText
                }
                stopPropagation={true}
              />

              <Checkbox
                disabled={!formState.values.publicEventsOnly}
                field="publicEventsOnlyOptional"
                id="public-events-only-optional"
                label={
                  messages.accessAndVisibilityVisibilityOptionPublicEventsOptional
                }
                labelTooltip={
                  messages.accessAndVisibilityVisibilityOptionPublicEventsOptionalHelpText
                }
                stopPropagation={true}
                style={{ marginTop: 5 }}
              />
            </FormGroup>
          </div>
        ) : null}

        {userIsSuperadmin && !hideAnalyticsControl ? (
          <div className="col">
            <FormGroup label={messages.commonAnalytics}>
              <Checkbox
                field="includeInAnalytics"
                id="include-in-analytics"
                label={messages.commonIncludeInAnalytics}
                stopPropagation={true}
              />
            </FormGroup>
          </div>
        ) : null}
      </div>

      {user?.roles.includes('vuoadmin') ? (
        <div className="row-sm">
          <div className="col">
            <FormGroup label={messages.commonEditingRights}>
              <Select
                field="editableBy"
                id="editable-by"
                options={[
                  { label: messages.usersAdmin, value: 'admin' },
                  { label: messages.usersSuperadmin, value: 'superadmin' },
                  { label: 'Vuoadmin', value: 'vuoadmin' }
                ]}
                style={{ maxWidth: 200 }}
              />
            </FormGroup>
          </div>
        </div>
      ) : null}

      <div className="row-sm">
        <div className="col-1of2">
          <FormGroup label={messages.commonType} maxWidth="200px">
            <Select
              field="type"
              id="type"
              options={Object.keys(attributeTypes).map((type) => ({
                label: attributeTypes[type],
                value: type
              }))}
            />
          </FormGroup>
        </div>
      </div>

      <div className="col-1of2">
        {['ArrayWithOptions', 'StringWithOptions'].includes(
          formState.values.type
        ) ? (
          <FormGroup
            label={messages.commonOptions}
            maxWidth="100%"
            style={{ marginTop: 15 }}
          >
            <SortableArrayList field="options" />
          </FormGroup>
        ) : formState.values.type === 'Number' ? (
          <FormGroup style={{ marginTop: 10 }}>
            <NumberStepper
              field="min"
              id="min"
              stepValue={1}
              label={messages.commonMinValue}
            />

            <NumberStepper
              field="max"
              id="max"
              label={messages.commonMaxValue}
              stepValue={1}
              style={{ marginTop: 10 }}
            />
          </FormGroup>
        ) : null}
      </div>
    </>
  );
};

export default ProductUserCustomAttributesForm;
